import { ApiError, api } from './api.js';
import { randomUUID } from './uuid.js';

/**
 * Identidade de um COMANDO do operador.
 *
 * ## O bug que isto corrige
 *
 * A primeira versão derivava a `Idempotency-Key` do ESTADO —
 * `['draw', roundId, ballsCount]` — o que parecia elegante e estava errado:
 *
 * 1. operador sorteia com 10 bolas no painel → chave `draw:r1:10`, sai o 37;
 * 2. operador **desfaz** → o painel volta a ter 10 bolas;
 * 3. operador sorteia de novo → a chave é `draw:r1:10` **de novo**;
 * 4. o servidor reconhece a chave e devolve o replay "saiu 37" **sem sortear**.
 *
 * Resultado: o `/controle` acha que saiu o 37, o telão não recebe evento nenhum
 * (não houve sorteio), e os dois ficam inconsistentes por até 10 minutos — o
 * TTL da chave. Desfazer-e-sortear é justamente a sequência que o operador faz
 * quando errou, ou seja, o bug aparece no pior momento possível.
 *
 * ## A regra
 *
 * A chave identifica a **intenção** (o toque), não o estado. Cada invocação do
 * comando gera um UUID novo; ele é reutilizado **apenas** nas retentativas
 * automáticas daquele mesmo envio, que é exatamente o caso que a idempotência
 * existe para resolver: a requisição saiu, a resposta se perdeu na rede do
 * salão, e reenviar não pode produzir uma segunda bola.
 *
 * Dois toques do operador são duas intenções e geram duas chaves — e duas
 * bolas, que é o comportamento certo. O que protege contra o duplo toque
 * acidental é o `disabled` do botão enquanto o comando está em voo, não a
 * idempotência.
 */

/** Quantas vezes reenviar o MESMO comando quando a rede falha. */
const NETWORK_RETRIES = 2;

export interface SendCommandOptions {
  method?: 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
}

/**
 * Envia um comando com identidade própria e retentativa segura.
 *
 * Só repete em **falha de rede** (a resposta nunca chegou). Erro HTTP é
 * resposta do servidor: ele já decidiu, e repetir seria pedir de novo o que foi
 * recusado.
 */
export async function sendCommand<T>(path: string, options: SendCommandOptions = {}): Promise<T> {
  // UUID gerado AQUI: uma vez por intenção.
  const idempotencyKey = randomUUID();

  let lastError: unknown;

  for (let attempt = 0; attempt <= NETWORK_RETRIES; attempt += 1) {
    try {
      return await api<T>(path, {
        method: options.method ?? 'POST',
        idempotencyKey,
        ...(options.body === undefined ? {} : { body: options.body }),
      });
    } catch (err) {
      lastError = err;

      // `ApiError` = o servidor respondeu. Não é retentativa, é decisão.
      if (err instanceof ApiError) throw err;
      if (attempt === NETWORK_RETRIES) throw err;

      await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
    }
  }

  throw lastError;
}
